import { useInfiniteQuery } from '@tanstack/react-query'

import { useSelectedProjectStore } from '@/components/entities/common/model/selected-project.store'
import { notificationTypeOptionsMap } from '@/components/entities/notification/ui/NotificationTypesFilter/NotificationTypesFilter.constants'
import {
  checkboxItemAll,
  checkboxItemUnread
} from '@/components/shared/checkbox/lib/constants'

import { NotificationsService } from '..'

export const NOTIFICATIONS_LIST_INFINITE_QUERY_KEY =
  'notifications-list-infinite'

type Props = {
  typeAliases?: string[]
  pageSize?: number
}

export const useNotificationsListInfinite = ({
  typeAliases,
  pageSize = 10
}: Props) => {
  const { selectedProjectId: projectId } = useSelectedProjectStore()

  const getFilteredTypeAlias = () => {
    const isOnlyUnreadSelected =
      !typeAliases ||
      (typeAliases.length === 1 && typeAliases[0] === checkboxItemUnread)

    const typeAliasList = isOnlyUnreadSelected
      ? notificationTypeOptionsMap.flatMap(typeAlias =>
          Array.isArray(typeAlias.value) ? typeAlias.value : [typeAlias.value]
        )
      : typeAliases

    return typeAliasList.filter(
      typeAlias =>
        typeAlias !== checkboxItemUnread && typeAlias !== checkboxItemAll
    )
  }

  return useInfiniteQuery({
    queryKey: [NOTIFICATIONS_LIST_INFINITE_QUERY_KEY, projectId, typeAliases],
    queryFn: async ({ pageParam }) => {
      const {
        data: { data, page, total, unreadCount }
      } = await NotificationsService.getNotificationsList({
        projectId,
        typeAlias: getFilteredTypeAlias(),
        page: pageParam,
        onlyUnread: typeAliases?.some(
          typeAlias => typeAlias === checkboxItemUnread
        ),
        pageSize
      })

      return {
        response: data,
        unreadCount,
        page: total - page * pageSize > 0 ? pageParam : undefined
      }
    },
    enabled: Boolean(projectId),
    select: response => {
      return {
        data: response.pages
          .flatMap(page => page.response)
          .filter(item => item !== null),
        unreadCount: response.pages[0].unreadCount
      }
    },
    initialPageParam: 1,
    getPreviousPageParam: lastPage =>
      lastPage.page === 0 ? lastPage.page - 1 : undefined,
    getNextPageParam: firstPage =>
      firstPage.page ? firstPage.page + 1 : undefined
  })
}
